import { useCallback, useEffect, useState } from "react";

// Real checks only: this page used to show Database and Push Notifications as "Operational" without testing them.
// App: can the browser reach it (a no-cors request only fails when the site can't be reached at all).
// Database: Skeulur's Supabase auth service answers its health check. The key is the app's public browser key.
const APP_URL = "https://skeulur1.rare-works.com";
const HEALTH_URL = "https://tmqgnhvupocjsuugtubx.supabase.co/auth/v1/health";
const PUBLIC_KEY = "sb_publishable_r_oqvgaqMozDpBTDBnITFA_ejulflGc";

type State = "checking" | "up" | "down";

const check = async (request: () => Promise<boolean>): Promise<State> => {
  try {
    return (await request()) ? "up" : "down";
  } catch {
    return "down";
  }
};

const LABEL: Record<State, string> = { checking: "Checking", up: "Operational", down: "Not responding" };
const DOT: Record<State, string> = { checking: "bg-gray-500", up: "bg-emerald-400", down: "bg-rose-500" };

const SkeulurStatus = () => {
  const [app, setApp] = useState<State>("checking");
  const [db, setDb] = useState<State>("checking");
  const [checkedAt, setCheckedAt] = useState("");

  const run = useCallback(async () => {
    setApp("checking");
    setDb("checking");
    const [a, d] = await Promise.all([
      check(async () => {
        await fetch(APP_URL, { method: "HEAD", mode: "no-cors", cache: "no-store" });
        return true;
      }),
      check(async () => (await fetch(HEALTH_URL, { headers: { apikey: PUBLIC_KEY }, cache: "no-store" })).ok),
    ]);
    setApp(a);
    setDb(d);
    setCheckedAt(
      new Date().toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" }),
    );
  }, []);

  useEffect(() => {
    run();
    const timer = setInterval(run, 60_000);
    return () => clearInterval(timer);
  }, [run]);

  const rows: [string, State][] = [
    ["Skeulur app", app],
    ["Database", db],
  ];
  const summary =
    app === "checking" || db === "checking"
      ? "Checking Skeulur now"
      : app === "up" && db === "up"
        ? "Skeulur is responding normally"
        : "Part of Skeulur is not responding";

  return (
    <section className="relative min-h-screen pt-32 pb-24">
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4">Skeulur Status</h1>
        <p className="text-xl text-gray-400 mb-12">A live check of the Skeulur scheduling app, run from your browser.</p>

        <div className="p-6 rounded-2xl glass mb-4">
          <p className="text-lg font-medium text-white mb-5" aria-live="polite">
            {summary}
          </p>
          <ul className="divide-y divide-white/10">
            {rows.map(([name, state]) => (
              <li key={name} className="flex items-center justify-between gap-4 py-3">
                <span className="text-gray-300">{name}</span>
                <span className="flex items-center gap-2 text-sm text-gray-400">
                  <span className={`w-2 h-2 rounded-full ${DOT[state]}`} />
                  {LABEL[state]}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
            <button
              type="button"
              onClick={run}
              className="px-4 py-2.5 rounded-xl bg-gray-800/60 border border-white/10 hover:border-teal-500/50 text-gray-200 text-sm font-medium transition-colors"
            >
              Check now
            </button>
            {checkedAt && <span className="text-sm text-gray-400">Last checked {checkedAt} Central</span>}
          </div>
        </div>

        <p className="text-gray-400 text-sm leading-relaxed">
          Skeulur is a RareWorks product.{" "}
          <a href={APP_URL} className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
            Open the app
          </a>
          {" or see the "}
          <a
            href="https://stats.uptimerobot.com/2wu6AK7wSl"
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-400 hover:text-teal-300 underline underline-offset-2"
          >
            uptime history
          </a>
          . Need help? Email{" "}
          <a href="mailto:alan@rare-works.com" className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
            alan@rare-works.com
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default SkeulurStatus;
